// Copyright (c) 2020-2021, 2023 Visual Intelligence LP. All rights reserved.
//
import { injectable } from 'inversify';
import { action, runInAction } from 'mobx';

import { Vendor, VendorDTO, VendorUser, VendorUserDTO, VendorUsersDTO } from '@shared/models/vendor';
import container from '@core/di';
import VendorsService from '@shared/services/vendors';
import BaseStore from '@core/stores/base';
import { ROLES } from '@shared/constants/user';
import { showNotification, NotificationType } from '@shared/components/Notification';

@injectable()
export default class VendorsStore extends BaseStore<VendorDTO, Vendor, VendorsService> {
  static diToken = Symbol('vendors-store');

  // setup di token and clear the loading status.
  constructor() {
    super({ service: container.get<VendorsService>(VendorsService.diToken) });

    const rolesLoading = Object.values(ROLES).reduce((acc, role) => ({ ...acc, [role]: false }), {});

    this.loading = {
      list: false,
      item: false,
      vendor: false,
      ...rolesLoading
    };
  }

  // Get a list of users (ID and name) with a given role by calling the service object.
  // Input:
  //   roleName: user roles such as pilot/observer/...
  //   onlyActive: whether to show only active users.
  @action getCompanyUsers = async (params: { roleName: string, onlyActive: boolean }): Promise<VendorUser[]> => {
    runInAction(() => {
      this.loading[params.roleName] = true; // set loading status for the role.
    });
    try {
      const users = await this.service.getCompanyUsers(params);
      runInAction(() => {
        this.loading[params.roleName] = false;  // clear loading status.
      });
      return users;
    } 
    catch {
      runInAction(() => {
        this.loading[params.roleName] = false;  // clear loading status.
      });
      return [];
    }
  }

  // Get a list of staff (ID and name) grouped by vendors by calling the service object.
  // Input:
  //   onlyActive: whether to show only active users.
  @action getVendorStaff = async (params: {onlyActive: boolean }): Promise<Array<VendorUsersDTO>> => {
    runInAction(() => {
      this.loading.vendor = true; // set loading status for the role.
    });

    try {
      const users = await this.service.getVendorStaff(params);
      runInAction(() => {
        this.loading.vendor = false;  // clear loading status.
      });
      return users;
    } 
    catch {
      runInAction(() => {
        this.loading.vendor = false;  // clear loading status.
      });
      return [];
    }
  }

  // Get a dictionary of users (ID and name) by roles by calling the service object.
  // Input: Array of:
  //   roleName: list of roles [pilot, observer, ...]
  //   onlyActive: whether to show only active users.
  // Return a dictionary with roleName as key and list of users as value.
  @action getCompanyUsersByRoles = async (rolesRequest: { roleName: string, onlyActive: boolean }[]) => {
    runInAction(() => {
      rolesRequest.forEach( role => this.loading[role.roleName] = true); // Set loading status for all roles in the array.
    });
    try {
      const usersByRoles = await this.service.getCompanyUsersByRoles(rolesRequest);
      runInAction(() => {
        rolesRequest.forEach( role => this.loading[role.roleName] = false); // Clear loading status.
      });
      return usersByRoles;
    } catch {
      runInAction(() => {
        rolesRequest.forEach( role => this.loading[role.roleName] = false); // Clear loading status.
      });
      showNotification('Failed to get users by roles', NotificationType.error);

      return {};
    }
  }

}